import React from 'react';
import useOpportunities from '@/hooks/swr/useOpportunities';
import useOpportunityConfidenceLevels from '@/hooks/swr/useOpportunityConfidenceLevels';
import useOpportunityStages from '@/hooks/swr/useOpportunityStages';
import { useQueryProvider } from '@/modules/QueryProvider';
import type Opportunity from '@/types/api/Opportunity';
import type { WithLaggy } from '@/utils/swr/laggy';
import laggy from '@/utils/swr/laggy';
import type OpportunitiesFilterParams from '../types/OpportunitiesFilterParams';
import mapQueryToFilter from '../utils/mapQueryToFilter';

export default function useOpportunitiesDataListData({
  constituentId,
  confidenceLevelId,
  opportunityStageId,
}: {
  confidenceLevelId?: string;
  constituentId?: string;
  opportunityStageId?: string;
  filters?: OpportunitiesFilterParams;
} = {}) {
  const { query } = useQueryProvider<OpportunitiesFilterParams>();

  const swrResponse = useOpportunities({
    filter: {
      constituentId,
      confidenceLevelId,
      opportunityStageId,
      ...mapQueryToFilter(query),
    },
    swrOptions: { use: [laggy] },
  });

  const { data: { opportunities, totalHits = 0 } = {}, isLagging } =
    swrResponse as WithLaggy<typeof swrResponse>;
  const { data: stagesData } = useOpportunityStages();
  const { data: confidenceLevelsData } = useOpportunityConfidenceLevels();

  const stageLookUp: {
    [k: string]: string;
  } | null = React.useMemo(() => {
    if (!stagesData) {
      return null;
    }

    return stagesData.opportunityStages.reduce(
      (acc, { name, opportunityStageId: stageId }) => ({
        ...acc,
        [stageId]: name,
      }),
      {},
    );
  }, [stagesData]);

  const confidenceLevelLookUp: {
    [k: string]: string;
  } | null = React.useMemo(() => {
    if (!confidenceLevelsData) {
      return null;
    }

    return confidenceLevelsData.opportunityConfidenceLevels.reduce(
      (acc, { confidenceLevelId: levelId, name }) => ({
        ...acc,
        [levelId]: name,
      }),
      {},
    );
  }, [confidenceLevelsData]);

  return React.useMemo(() => {
    if (!opportunities || !stageLookUp || !confidenceLevelLookUp) {
      return {
        isLagging,
        isLoading: true,
        opportunities: [],
        totalHits,
      };
    }

    return {
      isLagging,
      isLoading: false,
      opportunities: opportunities.map((opportunity: Opportunity) => {
        return {
          ...opportunity,
          stage: stageLookUp[opportunity.opportunityStageId],
          confidenceLevel: confidenceLevelLookUp[opportunity.confidenceLevelId],
        };
      }),
      totalHits,
    };
  }, [confidenceLevelLookUp, isLagging, opportunities, stageLookUp]);
}
